import type { EditorElement, ElementValue } from '@vedalib/editor/lib/elements'
import type { CSSProperties } from 'react'
import React, { useCallback, useMemo } from 'react'

import {
  useElementFeedbackCss,
  useElementFontCss,
  useElementStyleCss,
} from 'services/Branding/hooks'
import type { ElementStyleCss } from 'services/Branding/types'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getElementState } from 'services/Store/Project/selectors'
import type { Block, ElementState, IBlockMode } from 'services/Store/Project/types'

import Decorators from './decorators/Decorators'
import { getEditorElementComponent } from './elements.config'

const DEFAULT_SHELL_STYLES: React.CSSProperties = {
  width: '100%',
  height: '100%',
}

interface IEditorElementComponentProps {
  element: EditorElement
  mode: IBlockMode
  block?: Block
  shellStyles?: CSSProperties
  isV2?: boolean
  waiting?: boolean
  onChange?: (value: ElementValue) => void
}

const EditorElementComponent: React.FC<IEditorElementComponentProps> = ({
  block,
  element,
  shellStyles = DEFAULT_SHELL_STYLES,
  mode,
  waiting,
  isV2,
  onChange,
}) => {
  const dispatch = useProjectDispatch()
  const elementState = useProjectContext(getElementState, element.id)
  const Element = getEditorElementComponent(element.type)
  const elementStyles = useElementStyleCss(element.type, mode.deviceMode, element.style)
  const styles = useMemo(
    () => Object.assign({}, elementStyles, { shell: shellStyles }) as ElementStyleCss,
    [elementStyles, shellStyles],
  )

  const fonts = useElementFontCss(element.type, mode.deviceMode, element.font)
  const feedback = useElementFeedbackCss(element.type, mode.deviceMode, element.feedback)

  const setState = useCallback(
    (value: ElementState) => {
      if (element?.id) {
        dispatch(setElement({ id: element.id, value }))
      }
    },
    [dispatch, element?.id],
  )

  return (
    <Decorators>
      <div data-element-id={element.id} style={{ display: 'contents' }}>
        <Element
          block={block}
          element={element}
          feedback={feedback}
          font={fonts}
          isV2={isV2}
          mode={mode}
          onChange={onChange}
          setState={setState}
          state={elementState}
          styles={styles}
          waiting={waiting}
        />
      </div>
    </Decorators>
  )
}

export default React.memo(EditorElementComponent)
